import {
  extractVizElements,
  getBodyElementsFromLayout,
  getChartTokensFromElements,
  getLayoutGridWithVizTokens,
} from '@mode/shared/util-js';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { flatMap } from 'lodash';
import { createSelectorCreator, defaultMemoize } from 'reselect';
import { BreadcrumbTypes, SpaceType } from '@mode/shared/contract-common';
import * as fromReport from './report.reducer';
import { CollectionAccessLevel } from '@mode/shared/contract-common';
import { capraIconBuildingEdit, capraIconBuildingView, capraIconLock } from '@mode/capra';

function elementsEqual(a: Element[], b: Element[]) {
  if (a && b) {
    return a === b || (a.length === b.length && a.every((e, i) => e.outerHTML === b[i].outerHTML));
  }
  return false;
}

function getCollectionBreadcrumbTooltip(
  isPrivate: boolean,
  isViewable: boolean,
  defaultAccess: string,
  name: string
): string | undefined {
  const toolTipHelper = {
    toolTipIcons: {
      [CollectionAccessLevel.Edit]: capraIconBuildingEdit.data,
      [CollectionAccessLevel.View]: capraIconBuildingView.data,
      [CollectionAccessLevel.Restricted]: capraIconLock.data,
    },
    toolTipMessages: {
      personal: {
        [CollectionAccessLevel.Edit]: 'All Members can edit content you share from Personal',
        [CollectionAccessLevel.View]: 'All Members can view &amp; explore content you share from Personal',
        [CollectionAccessLevel.Restricted]: 'Only select Members can access content you share from Personal',
      },
      collection: {
        [CollectionAccessLevel.Edit]: 'All Members can edit Reports in this Collection',
        [CollectionAccessLevel.View]: 'All Members can view Reports in this Collection',
        [CollectionAccessLevel.Restricted]: 'Only select Members can access content in this Collection',
      },
    },
    toolTipTitles: {
      personal: 'Your Personal content',
      collection: `${name} Collection`,
    },
  };

  const toolTipType = isPrivate ? 'personal' : 'collection';

  const defaultAccessTooltip: any = (icon = '', heading = '', body = '') => `
    <div class="access-tooltip">
      <div>
        <span class="access-tooltip__icon">${icon}</span>
      </div>
      <div class="access-tooltip__desc">
        <strong class="access-tooltip__desc__heading">${heading}</strong>
        <p class="access-tooltip__desc__body">${body}</p>
      </div>
    </div>
  `;

  if (isPrivate && !isViewable) {
    // if the collection is personal and it is not viewable the user doesn't have access
    // add a tooltip explaining why
    return `<div class="access-tooltip-simple">Personal content cannot be visited by other Workspace Members</div>`;
  }

  if (!defaultAccess) {
    return '';
  }

  return defaultAccessTooltip(
    (toolTipHelper.toolTipIcons as any)[defaultAccess],
    (toolTipHelper.toolTipTitles as any)[toolTipType],
    (toolTipHelper.toolTipMessages as any)[toolTipType][defaultAccess]
  );
}

const createElementsSelector = createSelectorCreator(defaultMemoize, elementsEqual as any);

export const selectReportState = createFeatureSelector<fromReport.State>(fromReport.reportFeatureKey);

export const selectReport = createSelector(selectReportState, (state) => state.report);

export const selectReportIsLoading = createSelector(selectReportState, (state) => state.reportIsLoading);

export const selectReportIsComplete = createSelector(selectReportState, (state) => state.reportIsComplete);

export const selectReportDisplayName = createSelector(selectReportState, (state) =>
  state.report ? state.report.name || 'Untitled Report' : ''
);

export const selectReportCreatorName = createSelector(selectReportState, (state) => state.report?.creatorName);

export const selectReportIFrameSrc = createSelector(selectReportState, (state) => state.report?.iFrameSrc);

export const selectReportTheme = createSelector(selectReportState, (state) => state.report?.theme);

export const getLayout = createSelector(selectReportState, (state) => state.report?.layout || '');

export const getDatasetBreadCrumbs = createSelector(selectReportState, (state) => {
  const title: string = !state.report?.name ? 'Untitled Dataset' : (state.report?.name as string);
  return [
    state.report?.spaceType === SpaceType.private
      ? {
          type: BreadcrumbTypes.BreadcrumbType.Domain,
          icon: 'icon-avatar',
          name: state.report?.creatorName as string | undefined,
          href: `./${state.report.orgName}/spaces/${state.report?.spaceToken}`,
          self: true,
          tooltipContent: getCollectionBreadcrumbTooltip(
            true,
            state.report?.isViewable as boolean,
            state.report?.spaceDefaultAccess as string,
            title
          ),
        }
      : {
          type: BreadcrumbTypes.BreadcrumbType.Page,
          icon: 'icon-collection',
          name: state.report?.spaceName as string | undefined,
          href: `./${state.report?.orgName}/spaces/${state.report?.spaceToken}`,
          self: true,
          tooltipContent: getCollectionBreadcrumbTooltip(
            false,
            state.report?.isViewable as boolean,
            state.report?.spaceDefaultAccess as string,
            state.report?.spaceName as string
          ),
        },
    {
      type: BreadcrumbTypes.BreadcrumbType.Page,
      icon: 'icon-dataset',
      name: title,
      href: undefined,
      self: false,
    },
  ];
});

export const getEmpytReportBreadcrumbs = createSelector(selectReportState, (state) => {
  const title: string = !state.report?.name ? 'Untitled Report' : (state.report?.name as string);
  const homeUrl = './';
  return [
    {
      type: BreadcrumbTypes.BreadcrumbType.Domain,
      icon: 'icon-avatar',
      name: 'Personal',
      href: homeUrl,
      self: true,
      tooltipContent: getCollectionBreadcrumbTooltip(
        true,
        state.report?.isViewable as boolean,
        state.report?.spaceDefaultAccess as string,
        title
      ),
    },
    {
      type: BreadcrumbTypes.BreadcrumbType.Page,
      icon: 'icon-report-builder',
      name: title,
      href: undefined,
      self: false,
    },
  ];
});

export const parsedLayout = createSelector(getLayout, (layout) => {
  return getBodyElementsFromLayout(layout);
});

export const vizElementsInLayout = createElementsSelector(parsedLayout, (bodyElements) =>
  extractVizElements(bodyElements)
);

export const chartTokensInLayout = createElementsSelector(vizElementsInLayout, (visualElements) =>
  getChartTokensFromElements(visualElements)
);

export const layoutGridWithVizTokens = createElementsSelector(getLayout, (layout) =>
  getLayoutGridWithVizTokens(layout)
);

export const selectSelectedReportHasReportRun = createSelector(
  selectReportState,
  (state) => state.report?.hasSuccessfulReportRun
);

export const layoutHasScriptTag = createElementsSelector(parsedLayout, (bodyElements) => {
  const elements = flatMap(bodyElements);
  const scriptFlag = elements.some((e) =>
    e.nodeName === 'SCRIPT' || Array.from(e.querySelectorAll('SCRIPT')).length > 0 ? true : false
  );
  return scriptFlag;
});

export const hasVizElementsInLayout = createElementsSelector(vizElementsInLayout, (elements) => elements.length > 0);

export const isReportLayoutEmpty = createSelector(
  layoutHasScriptTag,
  hasVizElementsInLayout,
  (hasScriptTag, hasVizElementsInLayout) => {
    return !(hasScriptTag || hasVizElementsInLayout);
  }
);

export const headerReportData = createSelector(selectReportState, (state) => state?.headerReportData);

export const hasReportBeenRunAtLeastOnce = createSelector(headerReportData, (report) => {
  return !!report?.last_run_at;
});

export const headerReportToken = createSelector(headerReportData, (state) => state?.token);

export const displayNameForHeader = createSelector(headerReportData, (headerData) => headerData?.name || '');
